import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixWorkflowsV1Workflow from './workflows-v1-workflow-workflows.http.js';

export interface Workflow {
  /** Workflow information and metadata. */
  info?: WorkflowInfo;
  /**
   * Final phase of the workflow.
   * Cards in `winPhase` are treated as if they are not part of the workflow.
   *
   * Normally, a contact can be attached to only 1 card in the workflow.
   * Moving a contact's card to `winPhase`
   * frees up the contact to be attached to another card in the workflow.
   */
  winPhase?: Phase;
  /** Phases that make up the workflow. */
  phasesList?: PhasesList;
}

export interface WorkflowInfo {
  /**
   * Workflow ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** Display name for the workflow. */
  name?: string | null;
  /**
   * Workflow description,
   * shown below the workflow's display name in the dashboard.
   */
  description?: string | null;
  /**
   * @internal
   * @internal
   * @readonly
   * @deprecated
   */
  createdAt?: Date | null;
  /**
   * Indicates whether the workflow can be deleted.
   * @readonly
   * @deprecated
   */
  isDeletable?: boolean | null;
  /**
   * Date and time the workflow was created.
   * @readonly
   */
  _createdDate?: Date | null;
}

export interface Phase {
  /** Phase information. */
  info?: PhaseInfo;
  /** Cards contained in the phase. */
  cardsList?: CardsList;
}

export interface PhaseInfo {
  /**
   * Phase ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** Display name shown at the top of the phase. */
  name?: string | null;
}

/** a list of cards (a page) along with the total number */
export interface CardsList {
  /** Total number of cards in the phase. */
  total?: number;
  /**
   * List of cards in the phase.
   * Sorted according to the card display order, from top to bottom.
   */
  cards?: Card[];
}

export interface Card {
  /** Card details. */
  info?: CardInfo;
}

/** entity representing a card-info */
export interface CardInfo {
  /**
   * Card ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** Display name shown at the top of the card. */
  name?: string | null;
  /** Card description. */
  description?: string | null;
  /** Details about the contact attached to the card. */
  primaryAttachment?: Attachment;
  /** Due date. */
  dueDate?: Date | null;
  /** Name of the app or service that created the contact. */
  source?: string | null;
  /**
   * @internal
   * @internal
   * @readonly
   * @deprecated
   */
  createdAt?: Date | null;
  /**
   * @internal
   * @internal
   * @readonly
   * @deprecated
   */
  updatedAt?: Date | null;
  /**
   * ID of the phase that currently holds the card.
   * @format GUID
   * @readonly
   */
  phaseId?: string | null;
  /**
   * Date and time the card was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the card was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export interface Attachment {
  /**
   * Attachment ID. For internal use.
   * @format GUID
   * @readonly
   */
  attachmentId?: string;
  /**
   * @internal
   * @internal
   * @deprecated
   */
  value?: string;
  /**
   * @internal
   * @internal
   * @deprecated
   */
  attachmentType?: AttachmentTypeWithLiterals;
  /**
   * ID of the contact attached to the card.
   * @format GUID
   */
  contactId?: string;
}

/** describes the supported types of attachments (currently only supports Contact which is the default type) */
export enum AttachmentType {
  ContactType = 'ContactType',
}

/** @enumType */
export type AttachmentTypeWithLiterals = AttachmentType | 'ContactType';

/** a list of phases (a page) along with the total number */
export interface PhasesList {
  /** Total number of phases in the workflow, excluding `winPhase`. */
  total?: number;
  /**
   * List of phases in the workflow.
   * Sorted according to the phase display order, from left to right.
   */
  phases?: Phase[];
}

export interface ListWorkflowsRequest {
  /**
   * Number of workflows to return.
   * If omitted, all results are returned.
   */
  limit?: number | null;
  /**
   * Number of items to skip in the current sort order.
   *
   * Defaults to `0`.
   */
  offset?: number | null;
  /**
   * Supported fields:
   * `id`, `name`, `description`, `createdDate`
   *
   * List of fields to sort by.
   * Formatted as `field:direction`,
   * where field is the field name
   * and direction is `asc` (ascending) or `desc` (descending).
   *
   * Sorting is applied lexicographically, so `"abc"` comes after `"XYZ"`.
   */
  sort?: string[];
}

export interface ListWorkflowsResponse {
  /** Retrieved list of workflows. */
  workflows?: WorkflowInfo[];
  /** Metadata for the page of results. */
  pagination?: PaginationResponse;
}

export interface PaginationResponse {
  /** Number of items that were skipped in the current sort order. */
  offset?: number;
  /** Total number of items that matched the filter. */
  total?: number;
  /** Number of returned items. */
  count?: number;
}

export interface QueryWorkflowsRequest {
  /** Query options. */
  query?: Query;
}

export interface Query {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: any;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryWorkflowsResponse {
  /** List of workflows that matched the query. */
  workflows?: Workflow[];
  /** Metadata for the paginated results. */
  pagination?: PaginationResponse;
}

export interface GetWorkflowRequest {
  /**
   * ID of the workflow to retrieve.
   * @format GUID
   */
  _id: string;
  /**
   * Maximum number of cards to return per phase.
   * To retrieve additional pages of cards, use
   * [List Cards](../cards/listCards)
   * (in the Cards API).
   * @max 100
   */
  cardsPerPhase?: number | null;
}

export interface GetWorkflowResponse {
  /** Requested workflow. */
  workflow?: Workflow;
}

export interface UpdateWorkflowRequest {
  /**
   * ID of the workflow to update.
   * @format GUID
   */
  _id: string;
  /** Workflow info. */
  workflow?: WorkflowInfo;
}

export interface UpdateWorkflowResponse {}

export interface CreateWorkflowRequest {
  /** Workflow details. */
  workflow?: WorkflowInfo;
}

export interface CreateWorkflowResponse {
  /**
   * ID of the new workflow.
   * @format GUID
   */
  _id?: string | null;
}

export interface DeleteWorkflowRequest {
  /**
   * ID of the workflow to delete.
   * @format GUID
   */
  _id: string;
}

export interface DeleteWorkflowResponse {}

export interface SetupWorkflowRequest {}

export interface SetupWorkflowResponse {}

/**
 * Retrieves a list of the site's workflows info.
 *
 *
 * The `listWorkflows()` function returns a Promise that resolves to a list containing information about the site's workflows.
 *
 *
 * Use the `options` parameter to specify which workflows to retrieve and in which order to retrieve them. Workflows can be sorted based on their `"name"`.
 *
 *
 * If no `limit` parameter is passed, the first 100 workflows are returned. Sort order defaults to by `"name"` ascending.
 * @public
 * @documentationMaturity preview
 * @param options - Options to use when retrieving a list of workflows.
 * @permissionId workflows.view
 * @fqn com.wixpress.workflow.api.v1.WorkflowsService.ListWorkflows
 */
export async function listWorkflows(
  options?: ListWorkflowsOptions
): Promise<
  NonNullablePaths<
    ListWorkflowsResponse,
    `workflows` | `pagination.offset` | `pagination.total` | `pagination.count`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    limit: options?.limit,
    offset: options?.offset,
    sort: options?.sort,
  });

  const reqOpts = ambassadorWixWorkflowsV1Workflow.listWorkflows(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          limit: '$[0].limit',
          offset: '$[0].offset',
          sort: '$[0].sort',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListWorkflowsOptions {
  /**
   * Number of workflows to return.
   * If omitted, all results are returned.
   */
  limit?: number | null;
  /**
   * Number of items to skip in the current sort order.
   *
   * Defaults to `0`.
   */
  offset?: number | null;
  /**
   * Supported fields:
   * `id`, `name`, `description`, `createdDate`
   *
   * List of fields to sort by.
   * Formatted as `field:direction`,
   * where field is the field name
   * and direction is `asc` (ascending) or `desc` (descending).
   *
   * Sorting is applied lexicographically, so `"abc"` comes after `"XYZ"`.
   */
  sort?: string[];
}

/**
 * Retrieves a workflow by ID.
 *
 *
 * The `getWorkflow()` function returns a Promise that resolves to the workflow info with the specified ID.
 *
 *
 * This function requires you to specify the ID of a workflow. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
 * @param _id - ID of the workflow to retrieve.
 * @param cardsPerPhase - Maximum number of cards to return per phase.
 * To retrieve additional pages of cards, use
 * [List Cards](../cards/listCards)
 * (in the Cards API).
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField cardsPerPhase
 * @param options - Options to use when retrieving a workflow.
 * @permissionId workflows.view
 * @returns Requested workflow.
 * @fqn com.wixpress.workflow.api.v1.WorkflowsService.GetWorkflow
 */
export async function getWorkflow(
  _id: string,
  cardsPerPhase: number
): Promise<
  NonNullablePaths<
    Workflow,
    | `winPhase.cardsList.total`
    | `winPhase.cardsList.cards`
    | `winPhase.cardsList.cards.${number}.info.primaryAttachment.attachmentId`
    | `winPhase.cardsList.cards.${number}.info.primaryAttachment.value`
    | `winPhase.cardsList.cards.${number}.info.primaryAttachment.attachmentType`
    | `winPhase.cardsList.cards.${number}.info.primaryAttachment.contactId`
    | `phasesList.total`
    | `phasesList.phases`,
    8
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    cardsPerPhase: cardsPerPhase,
  });

  const reqOpts = ambassadorWixWorkflowsV1Workflow.getWorkflow(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.workflow!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]', cardsPerPhase: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'cardsPerPhase']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates an existing workflow.
 *
 *
 * The `updateWorkflow()` function returns a Promise that resolves when the workflow has been updated with the specified values.
 *
 *
 * Only the properties passed in the `workflow` object will be updated. All other properties will remain the same.
 *
 *
 * This function requires you to specify the ID of a workflow. To learn about retrieving IDs in the Workflow API, see [Retrieving IDs](#retrieving-ids).
 * @param _id - ID of the workflow to update.
 * @param workflow - Workflow info.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField workflow
 * @param options - Options to use when updating a workflow.
 * @permissionId workflows.modify
 * @fqn com.wixpress.workflow.api.v1.WorkflowsService.UpdateWorkflow
 */
export async function updateWorkflow(
  _id: string,
  workflow: WorkflowInfo
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    workflow: workflow,
  });

  const reqOpts = ambassadorWixWorkflowsV1Workflow.updateWorkflow(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]', workflow: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'workflow']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a new workflow.
 *
 *
 * The `createWorkflow()` function returns a Promise that resolves to the created workflow ID.
 * @param workflow - Workflow details.
 * @public
 * @documentationMaturity preview
 * @requiredField workflow
 * @requiredField workflow.name
 * @param options - Options to use when creating a workflow.
 * @permissionId workflows.modify
 * @fqn com.wixpress.workflow.api.v1.WorkflowsService.CreateWorkflow
 */
export async function createWorkflow(
  workflow: NonNullablePaths<WorkflowInfo, `name`, 2>
): Promise<CreateWorkflowResponse> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ workflow: workflow });

  const reqOpts = ambassadorWixWorkflowsV1Workflow.createWorkflow(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { workflow: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['workflow']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deletes a workflow.
 *
 *
 * The `deleteWorkflow()` function returns a Promise when the specified workflow has been deleted.
 *
 *
 * This function requires you to specify the ID of a workflow. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
 * @param _id - ID of the workflow to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId workflows.modify
 * @fqn com.wixpress.workflow.api.v1.WorkflowsService.DeleteWorkflow
 */
export async function deleteWorkflow(_id: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ id: _id });

  const reqOpts = ambassadorWixWorkflowsV1Workflow.deleteWorkflow(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
